import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { Client } from "@/features/clients/types";
import {
  type Form8821Data,
  type FormSubmission,
  MAX_TIA_MATTERS,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCheckbox } from "./formik-controls";
import { TiaAppointeesTable } from "./tia-appointees-table";
import { TiaMattersTable } from "./tia-matters-table";

const GENERATE_FOR_OPTIONS = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
] as const;

// 8821 is disclosure-only — the only toggles are which appointees receive
// copies (on the reps table) plus these authorization/retention options.
const OPTIONS: { name: keyof Form8821Data; label: string }[] = [
  {
    name: "auth_access",
    label: "Authorize access to my IRS records via an Intermediate Service Provider",
  },
  { name: "non_caf", label: "Specific use not recorded on the CAF" },
  { name: "dont_revoke", label: "Do not revoke prior Tax Information Authorizations" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse"]).required(),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(60, "Too long"),
        tax_form: Yup.string().max(40, "Too long"),
        tax_years: yearRange,
        tax_matters: Yup.string().max(100, "Too long"),
      }),
    )
    .max(MAX_TIA_MATTERS),
});

function toInitialValues(data: Record<string, unknown>): Form8821Data {
  const d = data as Partial<Form8821Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    matters:
      d.matters && d.matters.length > 0
        ? d.matters
        : [{ tax_type: "", tax_form: "", tax_years: "", tax_matters: "" }],
    auth_access: d.auth_access ?? false,
    non_caf: d.non_caf ?? false,
    dont_revoke: d.dont_revoke ?? false,
    signer: d.signer ?? "",
    signer_title: d.signer_title ?? "",
    member_ssn: d.member_ssn ?? "",
    plan_no: d.plan_no ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<Form8821Data["generate_for"]>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as Form8821Data["generate_for"])}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form 8821 (Tax Information Authorization) — disclosure-only: it
 * appoints appointee(s) who may *receive* the client's tax information (no
 * representation, so no "acts authorized" / exceptions section, unlike 2848).
 */
export function Form8821Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Appointee(s)</SectionHeading>
          <TiaAppointeesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Information</SectionHeading>
          <TiaMattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Authorization</SectionHeading>
          <div className="grid gap-3">
            {OPTIONS.map((o) => (
              <FormikCheckbox key={o.name} name={o.name} label={o.label} />
            ))}
          </div>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
